<template>
  <div class="q-mt-md">
    <!-- Header Of the page -->
    <h3 class="text-bold q-m-md inline-block">
    {{ bannerHeader }}
    </h3>
    <div class="row">
      <div class="col-8 q-mb-lg">
        <q-card v-if="getYellowBannerLoading" class="loading-wrapper">
          <div>
            <q-spinner color="primary" size="3em" />
          </div>
        </q-card>
        <q-card v-else class="timeline-card">
          <q-card-section>
            <q-tabs
              v-model="eventTab"
              dense
              class="text-grey"
              active-color="primary"
              indicator-color="primary"
              align="justify"
              inline-label
              @update:model-value="(val) => onTabChange(val)"
            >
              <q-tab name="newEventTab" label="New">
                <font-awesome-icon
                  icon="fa-add"
                  size="xl"
                  color="primary"
                  class="q-ml-sm"
                />
              </q-tab>
              <q-tab name="editEventTab" label="Edit ">
                <font-awesome-icon
                  icon="fa-edit"
                  size="xl"
                  color="primary"
                  class="q-ml-sm"
                />
              </q-tab>
            </q-tabs>
            <q-separator />
          </q-card-section>

          <q-card-section>
            <!-- Toggle to show/hide yellow banner on AARP's site -->
            <q-toggle
              class="q-mb-md"
              :color="getEnv === 'stage' ? 'secondary' : 'primary'"
              label="Show Yellow Banner"
              v-model="yellowBannerObject.showNotification"
            />

            <!-- Current Yellow banner from AARP site, updated when the TextArea changes -->
            <q-slide-transition>
              <div v-show="yellowBannerObject.showNotification">
                <strong>Preview:</strong>
                <YellowBanner
                  :key="yellowBannerObject"
                  :content="yellowBannerObject.text"
                />
              </div>
            </q-slide-transition>

            <div class="row q-mt-lg">
              <div class="col-7">
                <div>
                  <q-input
                    outlined
                    v-model="yellowBannerObject.eventName"
                    label="Event Name"
                    :rules="[
                      (val) =>
                        val.length >= 3 || 'Please use Minimum 3 characters',
                    ]"
                    lazy-rules
                  />
                </div>

                <GamesFilter
                  :value="yellowBannerObject.gameIDs"
                  @update:value="(val) => (yellowBannerObject.gameIDs = val)"
                  :disabled="yellowBannerObject.stats === 'closed'"
                  :edit="updateGamesFilterKey"
                />

                <!-- HTML Tips/shortcut to put on banner -->
                <div class="q-mt-md">
                  <strong>{{ bannerMessage }}</strong>
                </div>

                <!-- TextArea with current Text on AARP site, changeable to insert a new one -->
                <q-input
                  v-model="yellowBannerObject.text"
                  class="textarea-content"
                  outlined
                  dense
                  type="textarea"
                  :rules="[
                    (val) =>
                      val.length >= 3 || 'Please use Minimum 3 characters',
                  ]"
                  lazy-rules
                />

                <HtmlTip />
              </div>

              <div class="col-5 flex justify-center date">
                <q-date
                  :color="getEnv === 'stage' ? 'secondary' : 'primary'"
                  :text-color="getEnv === 'stage' ? 'black' : 'white'"
                  v-model="selectedEventDatePicker"
                  range
                  mask="MM/DD/YYYY"
                  :options="getFreeEventDates(this.currentEvent, this.yellowBannerEvents)"
                  @update:model-value="(val) => onChangeEventDate(val)"
                />
              </div>
            </div>
            <div class="q-mt-lg flex justify-between">
              <div>
                <q-btn
                  v-if="eventTab === 'newEventTab'"
                  label="Add Event"
                  @click="createEventModal = true"
                  color="positive"
                  :disable="handlerDisableCreateEvent()"
                />
                <q-btn
                  v-else
                  label="Edit Event"
                  @click="createEventModal = true"
                  color="positive"
                  :disable="handlerDisableCreateEvent()"
                />

                <q-btn
                  v-if="getEnv === 'stage' && isProdEnv"
                  class="q-ml-md"
                  color="primary"
                  label="switch to production"
                  @click="sendToProd"
                  :disable="yellowBannerObject.eventName.length < 3"
                >
                  <q-tooltip self="center left">
                    Send fields to Production environment</q-tooltip
                  >
                  <font-awesome-icon
                    class="q-ml-md"
                    icon="fa-share"
                    size="lg"
                    :color="
                      yellowBannerObject.eventName.length < 3 ? 'gray' : 'white'
                    "
                  />
                </q-btn>
              </div>
              <div>
                <q-btn
                  v-if="eventTab === 'editEventTab'"
                  label="Duplicate Event"
                  @click="() => duplicateEvent(this.yellowBannerObject)"
                  color="primary"
                />
                <q-btn
                  v-else
                  label="reset"
                  @click="
                    () => {
                      this.resetEventModal = true;
                    }
                  "
                  color="primary"
                />
                <q-btn
                  class="q-ml-sm"
                  v-if="
                    eventTab === 'editEventTab' &&
                    this.yellowBannerObject.stats !== 'closed' &&
                    this.yellowBannerObject.stats !== ''
                  "
                  @click="deleteEventModal = true"
                  color="negative"
                >
                  <font-awesome-icon icon="fa-trash" size="sm" color="negative"
                /></q-btn>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
      <div class="col-4 q-pl-md">
        <q-card class="timeline-card">
          <q-card-section class="timeline-card-section">
            <div class="flex no-wrap items-center justify-between">
              <q-input
                v-if="!getYellowBannerLoading"
                borderless
                v-model="searchText"
                input-class="text-left"
                class="q-ml-md"
                :label="
                  getEnv === 'prod' ? 'Production Events' : 'Stage Events'
                "
                @update:model-value="(val) => onSearchChange(val)"
              >
                <template v-slot:prepend>
                  <font-awesome-icon
                    v-if="searchText === ''"
                    icon="fa-search"
                    size="sm"
                    color="primary"
                  />
                  <font-awesome-icon
                    v-else
                    icon="fa-xmark"
                    size="xl"
                    color="primary"
                    name="clear"
                    class="cursor-pointer"
                    @click="() => onSearchChange('')"
                  />
                </template>
              </q-input>
              <q-btn round color="primary" v-if="!getYellowBannerLoading">
                <font-awesome-icon
                  icon="fa-calendar-days"
                  size="xl"
                  color="primary"
                />
                <q-popup-proxy
                  @before-show="updateProxyDateInTimeline"
                  transition-show="scale"
                  transition-hide="scale"
                >
                  <q-date
                    v-model="proxyDate"
                    mask="MM/DD/YYYY"
                    color="grey-2"
                    text-color="black"
                    :options="availableEventsDates"
                  >
                    <div class="row items-center justify-end q-gutter-sm">
                      <q-btn
                        label="Cancel"
                        color="primary"
                        flat
                        v-close-popup
                      />
                      <q-btn
                        label="OK"
                        color="primary"
                        flat
                        @click="setEventSearchDate"
                        v-close-popup
                      />
                    </div>
                  </q-date>
                </q-popup-proxy>
              </q-btn>
            </div>
          </q-card-section>

          <q-card-section v-if="getYellowBannerLoading">
            <div class="q-ma-md">
              <q-skeleton height="15px" />
              <q-skeleton height="40px" class="q-mt-md" />
            </div>
            <div class="q-ma-md">
              <q-skeleton height="15px" />
              <q-skeleton height="40px" class="q-mt-md" />
            </div>
            <div class="q-ma-md">
              <q-skeleton height="15px" />
              <q-skeleton height="40px" class="q-mt-md" />
            </div>
            <div class="q-ma-md">
              <q-skeleton height="15px" />
              <q-skeleton height="40px" class="q-mt-md" />
            </div>
          </q-card-section>
          <q-card-section v-else>
            <EmptyList :listIsEmpty="!yellowBannerEvents.length" :searchTextIsEmpty="!searchText.length" />
            <q-timeline>
              <q-timeline-entry
                v-for="eventBanner in yellowBannerEvents"
                :key="eventBanner.eventKey"
                class="timeline-entry"
                @click="() => timelineEventChange(eventBanner)"
                side="left"
                :title="eventBanner.eventName"
                :subtitle="
                  displayDatesInTimeline(
                    eventBanner.eventDateStart,
                    eventBanner.eventDateEnd
                  )
                "
                :color="eventBanner.timeLineColor"
              >
                <template v-slot:title>
                  {{ eventBanner.eventName }}
                </template>
              </q-timeline-entry>
            </q-timeline>
          </q-card-section>
        </q-card>
      </div>
    </div>

    <q-dialog v-model="deleteEventModal">
      <q-card class="card-modal">
        <q-card-section>
          <h6 class="q-ml-sm">{{ deleteModalText }}</h6>
          <strong class="q-ml-sm"> ({{ yellowBannerObject.eventName.toUpperCase() }}) </strong>
        </q-card-section>

        <q-card-actions class="flex justify-between q-ma-md">
          <q-btn flat label="Cancel" color="primary" v-close-popup />
          <q-btn label="Delete" color="primary" @click="deleteEventConfirm" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="createEventModal">
      <q-card class="card-modal">
        <q-card-section>
          <h6 class="q-ml-sm">
            {{
              !this.yellowBannerObject.eventKey
                ? createModalText
                : editModalText
            }}
          </h6>
          <strong class="q-ml-sm"> ({{ yellowBannerObject.eventName.toUpperCase() }}) </strong>
        </q-card-section>

        <q-card-actions class="flex justify-between q-ma-md">
          <q-btn flat label="Cancel" color="primary" v-close-popup />
          <q-btn label="Confirm" color="positive" @click="createEventConfirm" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="resetEventModal">
      <q-card class="card-modal">
        <q-card-section>
          <h6 class="q-ml-sm">{{ resetModalText }}</h6>
          <strong class="q-ml-sm"> ({{ this.yellowBannerObject.eventName.toUpperCase() }}) </strong>
        </q-card-section>

        <q-card-actions class="flex justify-between q-ma-md">
          <q-btn flat label="Cancel" color="primary" v-close-popup />
          <q-btn label="Reset" color="primary" @click="() => {
            this.resetEventModal = false;
            newEvent()
          }" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import YellowBanner from "@/components/yellowBanner/YellowBanner.vue";
import { QTimeline } from "quasar";
import { errorMessage } from "../errorMessages";
import {
  convertDateIntoAmericanFormat,
  getEventByDate,
  datePickerTimelineOptionsFn,
  getFreeEventDates
} from "@/utils";
import HtmlTip from "@/components/html-tip/HtmlTip.vue";
import EmptyList from "@/components/empty-list/EmptyList.vue";
import GamesFilter from "../components/games-filter/GamesFilter.vue";

export default {
  name: "YellowBannerView",
  components: {
    QTimeline,
    YellowBanner,
    HtmlTip,
    EmptyList,
    GamesFilter
  },
  data() {
    return {
      isProdEnv: false,
      bannerHeader: "Yellow Banner",
      bannerMessage: "Yellow Banner Text (HTML): ",

      updateGamesFilterKey: false,
      yellowBannerEvents: [],
      yellowBannerObject: {
        text: "",
        showNotification: true,
        eventName: "",
        eventDateStart: "",
        eventDateEnd: "",
        stats: "",
        gameIDs: [],
      },

      initialDate: null,
      proxyDate: null,
      yellowBannerEventsFirstDate: "",
      yellowBannerEventsLastDate: "",
      selectedEventDatePicker: { from: "", to: "" },
      searchText: "",
      eventTab: "newEventTab",

      deleteEventModal: false,
      createEventModal: false,
      resetEventModal: false,
      createModalText: "Are you sure you want to Create the Event?",
      editModalText: "Are you sure you want to Edit the Event?",
      deleteModalText: "Are you sure you want to DELETE the Event?",
      resetModalText: "Are you sure you want to RESET the Event?",
      errorMessage,

      convertDateIntoAmericanFormat,
      getEventByDate,
      datePickerTimelineOptionsFn,
      getFreeEventDates
    };
  },

  computed: {
    ...mapGetters({
      getYellowBannerEvents: "yellowBanner/getYellowBannerEvents",
      getYellowBannerLoading: "yellowBanner/getYellowBannerLoading",
    }),
    ...mapGetters({
      getEnv: "selectEnv/getEnv",
    }),
    ...mapGetters({
      getYellowBannerToProd: "generalPersist/getYellowBannerToProd",
    }),
  },

  async created() {
    this.isProdEnv = process.env.NODE_ENV === "production";
  },
  async mounted() {
    await this.getAllBannerEvents();
    // Check if the component came from StageToProd and fill with previous stage infos
    if (this.getYellowBannerToProd.eventName && this.getEnv === "prod") {
      this.yellowBannerObject = {
        text: this.getYellowBannerToProd.text,
        showNotification: this.getYellowBannerToProd.showNotification,
        eventName: this.getYellowBannerToProd.eventName,
        eventDateStart: "",
        eventDateEnd: "",
        stats: "",
        gameIDs: this.getYellowBannerToProd.gameIDs
      };
      this.eventTab = "newEventTab";
      // Clear persistDate after we fill the form
      this.setYellowBannerToProd({});
    } else {
      this.newEvent();
    }

    // clear persistDate if is Stage
    if (this.getEnv === "stage") {
      this.setYellowBannerToProd({});
    }
  },

  methods: {
    ...mapActions({
      setYellowBannerToProd: "generalPersist/setYellowBannerToProd",
    }),
    ...mapActions({
      setEnv: "selectEnv/setEnv",
    }),
    ...mapActions({
      actionGetYellowBannerEventsList:
        "yellowBanner/actionGetYellowBannerEventsList",
      actionSetYellowBanner: "yellowBanner/actionSetYellowBanner",
      actionDeleteBannerEvent: "yellowBanner/actionDeleteBannerEvent",
    }),

    newEvent() {
      this.yellowBannerObject = {
        text: "",
        showNotification: true,
        eventName: "",
        eventDateStart: "",
        eventDateEnd: "",
        stats: "",
        gameIDs: [],
      };
      this.eventTab = "newEventTab";
    },
    async editEvent() {
      if (this.yellowBannerObject) {
        this.selectedEventDatePicker = {
          from: this.convertDateIntoAmericanFormat(
            this.yellowBannerObject.eventDateStart
          ),
          to: this.convertDateIntoAmericanFormat(
            this.yellowBannerObject.eventDateEnd
          ),
        };
      } else {
        this.newEvent();
      }
      this.eventTab = "editEventTab";
    },

    // duplicate fields to prod env
    sendToProd() {
      this.setYellowBannerToProd({
        text: this.yellowBannerObject.text,
        showNotification: this.yellowBannerObject.showNotification,
        eventName: this.yellowBannerObject.eventName,
        eventDateStart: "",
        eventDateEnd: "",
        stats: "",
        gameIDs: this.yellowBannerObject.gameIDs
      });
      this.setEnv("prod");
    },

    // Set event for today's event when user change the tab from new to edit
    onTabChange(val) {
      if (val === "newEventTab") {
        this.newEvent();
      } else {
        const currentEvent = _.cloneDeep(
          this.getEventByDate(
            new Date().toDateString(),
            this.yellowBannerEvents
          )
        );
        if (currentEvent) {
          this.yellowBannerObject = _.cloneDeep(currentEvent);
          this.editEvent();
        } else {
          this.newEvent();
        }
      }
    },

    // Handle filter events by text input
    onSearchChange(val) {
      if (val === "") {
        this.searchText = "";
      }
      this.resetBannerEvents();
      this.yellowBannerEvents = this.yellowBannerEvents.filter((product) => {
        return product.eventName.toLowerCase().indexOf(val.toLowerCase()) != -1;
      });
    },
    resetBannerEvents() {
      this.yellowBannerEvents = _.cloneDeep(this.getYellowBannerEvents);
    },

    // Mask to show dates in timeline
    displayDatesInTimeline(dateStart, dateEnd) {
      return (
        new Date(dateStart).toLocaleDateString("en-us", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }) +
        " - " +
        new Date(dateEnd).toLocaleDateString("en-us", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      );
    },
    // Select event by clicking in the timeline
    timelineEventChange(currentEvent) {
      const cloneEvent = _.cloneDeep(currentEvent);
      this.yellowBannerObject = { ...cloneEvent, gameIDs: cloneEvent.gameIDs ?? [] }; 
      // Update the initial date into a american format, to be displayed in the datepicker
      this.initialDate = this.convertDateIntoAmericanFormat(
        currentEvent.eventDateStart
      );
      this.editEvent();
    },
    // Date picker to return available range days with promo events
    availableEventsDates(date) {
      return this.datePickerTimelineOptionsFn(
        date,
        this.yellowBannerEventsFirstDate,
        this.yellowBannerEventsLastDate
      );
    },
    // Save new date in datepicker
    setEventSearchDate() {
      let bannerEvent = this.getEventByDate(
        this.proxyDate,
        this.yellowBannerEvents
      );
      if (!bannerEvent) {
        this.$q.notify({
          type: "warning",
          message: `There's no Event at: ${this.convertDateIntoAmericanFormat(
            this.proxyDate
          )}`,
        });
        this.newEvent();
      } else {
        this.yellowBannerObject = _.cloneDeep(bannerEvent);
        this.editEvent();
      }
      this.initialDate = this.convertDateIntoAmericanFormat(this.proxyDate);
    },
    // Initialize datepicker with initial date
    updateProxyDateInTimeline() {
      this.proxyDate = this.convertDateIntoAmericanFormat(this.initialDate);
    },

    // Duplicate an event without edit it
    duplicateEvent(oldBannerObj) {
      this.eventTab = "newEventTab";
      this.yellowBannerObject = {
        text: oldBannerObj.text,
        showNotification: oldBannerObj.showNotification,
        eventName: oldBannerObj.eventName,
        eventDateStart: "",
        eventDateEnd: "",
        stats: "",
        gameIDs: oldBannerObj.gameIDs
      };
    },

    // Set new dates in the datepicker
    onChangeEventDate(val) {
      if (!val) {
        this.yellowBannerObject.eventDateStart = "";
        this.yellowBannerObject.eventDateEnd = "";
      } else if (typeof val === "string") {
        this.yellowBannerObject.eventDateStart = val;
        this.yellowBannerObject.eventDateEnd = val;
      } else {
        this.yellowBannerObject.eventDateStart = val.from;
        this.yellowBannerObject.eventDateEnd = val.to;
      }
    },

    // Disable event if
    handlerDisableCreateEvent() {
      return (
        this.yellowBannerObject.eventName.length < 3 ||
        this.yellowBannerObject.text.length < 3 ||
        this.yellowBannerObject.eventDateStart === "" ||
        this.yellowBannerObject.eventDateEnd === "" ||
        this.yellowBannerObject.text === "" ||
        this.yellowBannerObject.stats === "closed"
      );
    },

    async getAllBannerEvents() {
      this.initialDate = new Date().toDateString();
      this.proxyDate = new Date().toDateString();
      await this.actionGetYellowBannerEventsList(this.getEnv)
        .then((response) => {
          if (response) {
            this.yellowBannerEvents = _.cloneDeep(response);
          }
          this.yellowBannerEventsFirstDate = new Date(
            this.yellowBannerEvents[
              this.yellowBannerEvents.length - 1
            ].eventDateStart
          ).toDateString();
          this.yellowBannerEventsLastDate = new Date(
            this.yellowBannerEvents[0].eventDateEnd
          ).toDateString();
        })
        .catch((err) => {
          console.log("err: ", err);
        });
    },
    async createEventConfirm() {
      this.createEventModal = false;
      await this.actionSetYellowBanner(this.yellowBannerObject)
        .then(async () => {
          await this.getAllBannerEvents();
          this.$q.notify({
            type: "positive",
            message: `The event was successfully ${
              this.yellowBannerObject.eventKey ? "Edited" : "Created"
            }`,
          });
          this.newEvent();
        })
        .catch((err) => {
          this.$q.notify({
            type: "negative",
            message: err.response.data.details[0].Message,
          });
        });
      this.onSearchChange("");
    },
    async deleteEventConfirm() {
      this.deleteEventModal = false;
      await this.actionDeleteBannerEvent(this.yellowBannerObject.eventKey)
        .then(async () => {
          await this.getAllBannerEvents();
          this.$q.notify({
            type: "positive",
            message: `The event was successfully Deleted`,
          });
          this.newEvent();
        })
        .catch(() => {
          this.$q.notify({
            type: "negative",
            message: `An error occurred trying to Delete`,
          });
        });
      this.onSearchChange("");
    },
  },
  // Do some changes once the text is changed
  watch: {
    yellowBannerObject() {
      this.selectedEventDatePicker = {
        from: this.convertDateIntoAmericanFormat(
          this.yellowBannerObject.eventDateStart
        ),
        to: this.convertDateIntoAmericanFormat(
          this.yellowBannerObject.eventDateEnd
        ),
      };
      this.updateGamesFilterKey = !this.updateGamesFilterKey;
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-wrapper {
  min-height: 600px;
  position: relative;
  div {
    position: absolute;
    left: 50%;
    top: 10%;
  }
}

.textarea-content {
  max-width: 1140px;
}

// Tips class inside TipsBanner component
.tips {
  border-radius: 4px;
  width: max-content;
  padding: 1em;
  background-color: #c2f8ff;
}

.tips-icon {
  cursor: pointer;
}

.timeline-card {
  position: relative;
  max-height: 80vh;
  overflow: scroll;
  overflow-x: hidden;
}

.timeline-entry {
  cursor: pointer !important;
}
.timeline-card-section {
  position: sticky;
  min-height: 80px;
  top: 0;
  background-color: rgb(241, 241, 241);
  z-index: 2;
  width: 100%;
}

.card-modal {
  width: 600px;
  max-width: 80vw;
}

.date {
  height: max-content;
}
</style>
